import type { IsoDate } from "./types.js";
import { addDays, daysBetween } from "./dates.js";
import { TARGET_END_DATE } from "./plan.js";

/**
 * 身體數值的平均與線性外推。前後端共用。
 *
 * 放在 `shared/` 而不是 `src/domain/`：監督者看到的升降方向由**伺服器**算
 * （死區是公斤，而前端拿到的是無單位值，見 `docs/adr/0004`），而
 * `functions/` 編譯不到 `src/`。另寫一份會讓同一個平均公式有兩個來源。
 *
 * 與 `src/domain/` 的鐵則一致：**不讀系統時間、不讀全域狀態**，「今天」由
 * 呼叫端傳入。
 */

/**
 * 這幾支函式真正需要的欄位。
 *
 * 刻意不直接用 `BodyMeasurementRow` 或 `src/domain` 的 `Measurement`——
 * 兩者對 `measured_at` / `source` / `updated_at` 的必填與否不同，綁死任一個
 * 都會讓另一邊過不了型別。取交集，兩邊都塞得進來。
 */
export interface MeasureRow {
  measurement_date: IsoDate;
  weight_kg: number | null;
  body_fat_percent: number | null;
  lean_body_mass_kg: number | null;
  bmi: number | null;
}

export type MeasurementKey = "weight_kg" | "body_fat_percent" | "lean_body_mass_kg" | "bmi";

/** 忽略 null 與 undefined 的平均。全部都沒有值時回 null，不是 0。 */
export function average(values: (number | null | undefined)[]): number | null {
  const nums = values.filter((value): value is number => Number.isFinite(value as number));
  return nums.length ? nums.reduce((sum, value) => sum + value, 0) / nums.length : null;
}

/**
 * 最新一筆「有體重」的測量。沒有體重的紀錄會被跳過。
 *
 * 泛型是為了讓呼叫端拿回自己傳進來的那個型別——傳 `Measurement[]` 就拿回
 * `Measurement`，不會被窄化成 `MeasureRow`。
 */
export function latestMeasurement<T extends MeasureRow>(rows: T[]): T | null {
  return [...rows].reverse().find(item => Number.isFinite(item.weight_kg)) || null;
}

export function recentMeasurements<T extends MeasureRow>(
  rows: T[],
  today: IsoDate,
  days = 28
): T[] {
  const cutoff = addDays(today, -days + 1);
  return rows.filter(item => item.measurement_date >= cutoff);
}

/**
 * 最近 N 日的平均。
 *
 * 錨點是「最新測量日」而不是「今天」——沒有新測量時，平均值不該因為
 * 日子往後推就改變。
 */
export function avgLast<T extends MeasureRow>(
  rows: T[],
  key: MeasurementKey,
  days: number
): number | null {
  const latest = latestMeasurement(rows);
  if (!latest) return null;
  const start = addDays(latest.measurement_date, -days + 1);
  return average(
    rows
      .filter(item => item.measurement_date >= start && item.measurement_date <= latest.measurement_date)
      .map(item => item[key])
  );
}

/** 再前一個 N 日區間的平均，用來跟 avgLast 比較出「本週變化」。 */
export function previousAvg<T extends MeasureRow>(
  rows: T[],
  key: MeasurementKey,
  days: number
): number | null {
  const latest = latestMeasurement(rows);
  if (!latest) return null;
  const end = addDays(latest.measurement_date, -days);
  const start = addDays(end, -days + 1);
  return average(
    rows
      .filter(item => item.measurement_date >= start && item.measurement_date <= end)
      .map(item => item[key])
  );
}

export interface Prediction {
  slope: number;
  predicted: number;
}

/**
 * 依最近 28 日的線性趨勢外推到生日目標日。
 *
 * 這是單純的線性外推，不是預測模型——體重不會一直線性下降，
 * 數字只作為「目前速度大致朝哪裡走」的參考。
 */
export function linearPrediction<T extends MeasureRow>(
  rows: T[],
  today: IsoDate
): Prediction | null {
  const recent = recentMeasurements(rows, today, 28)
    .filter((item): item is T & { weight_kg: number } => Number.isFinite(item.weight_kg));
  if (recent.length < 7) return null;

  const xs = recent.map((_, index) => index);
  const ys = recent.map(row => row.weight_kg);
  const xMean = average(xs) as number;
  const yMean = average(ys) as number;

  const denominator = xs.reduce((sum, x) => sum + (x - xMean) ** 2, 0);
  if (!denominator) return null;

  const slope = xs.reduce((sum, x, index) => sum + (x - xMean) * (ys[index] - yMean), 0) / denominator;
  const last = recent.at(-1) as T & { weight_kg: number };
  const days = daysBetween(last.measurement_date, TARGET_END_DATE);
  return { slope, predicted: last.weight_kg + slope * days };
}
